import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import SEOMeta from "@/components/SEOMeta";
import NavbarNew from "@/components/NavbarNew";
import FooterNew from "@/components/FooterNew";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "lucide-react";
import {
  useProductCatalogItem,
  PRODUCT_STATUS_CLASS,
  PRODUCT_STATUS_LABEL,
} from "@/hooks/useProductsCatalog";

const ProdutoDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: product, isLoading, isError } = useProductCatalogItem(slug);

  useEffect(() => {
    if (!isLoading && (isError || !product)) {
      navigate("/produtos", { replace: true });
    }
  }, [isLoading, isError, product, navigate]);

  if (isLoading || !product) {
    return (
      <div className="min-h-screen bg-background">
        <NavbarNew />
        <main>
          <div className="mx-auto max-w-[1000px] px-8 pb-[90px] pt-14 max-[900px]:px-5">
            <Skeleton className="mb-8 h-4 w-32" />
            <Skeleton className="mb-4 h-10 w-2/3" />
            <Skeleton className="mb-2 h-4 w-full" />
            <Skeleton className="mt-8 aspect-[16/10] w-full rounded-[18px]" />
          </div>
        </main>
        <FooterNew />
      </div>
    );
  }

  const [priceLine, priceNote] = (product.pricing || "").split("·").map((s) => s.trim());
  const included = Array.isArray(product.included) ? product.included : [];

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta
        title={`${product.name} | Produtos | Eliveuton Souza`}
        description={product.description || undefined}
        canonicalUrl={`/produtos/${product.slug}`}
        ogImage={product.image_url || undefined}
      />
      <NavbarNew />

      <main>
        <div className="mx-auto max-w-[1000px] px-8 pb-[90px] pt-14 max-[900px]:px-5">
          <Link
            to="/produtos"
            className="inline-flex items-center gap-[7px] text-[13px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-[15px] w-[15px]" />
            Todos os produtos
          </Link>

          <div className="mt-7 grid grid-cols-[1.1fr_.9fr] gap-11 max-[900px]:grid-cols-1">
            <div>
              <div className="flex flex-wrap items-center gap-[9px]">
                <span
                  className={`inline-flex h-[25px] items-center rounded-lg px-[11px] text-[11px] font-bold ${PRODUCT_STATUS_CLASS[product.status]}`}
                >
                  {PRODUCT_STATUS_LABEL[product.status]}
                </span>
                {product.stack && (
                  <span className="font-mono text-[11.5px] text-muted-foreground">
                    {product.stack}
                  </span>
                )}
              </div>
              <h1 className="mt-4 text-[40px] font-bold leading-[1.1] tracking-[-0.04em] max-[900px]:text-[38px]">
                {product.name}
              </h1>
              <p className="mt-4 text-[17px] leading-[1.7] text-muted-foreground">
                {product.description}
              </p>

              <div className="mt-[30px] aspect-[16/10] overflow-hidden rounded-[18px] border border-border bg-card">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full [background-image:repeating-linear-gradient(135deg,hsl(var(--border))_0_8px,transparent_8px_16px)]" />
                )}
              </div>

              {product.features.length > 0 && (
                <>
                  <h2 className="mb-4 mt-9 text-[23px] font-bold tracking-[-0.025em]">
                    O que ele faz
                  </h2>
                  <div className="flex flex-col gap-[11px]">
                    {product.features.map((feature) => (
                      <div
                        key={feature}
                        className="flex items-start gap-3 rounded-[13px] border border-border bg-card px-[17px] py-[15px]"
                      >
                        <span className="mt-px flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-primary">
                          ✓
                        </span>
                        <span className="text-[14px] leading-[1.55]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div>
              <div className="sticky top-[92px] flex flex-col gap-4">
                <div className="rounded-[20px] border-[1.5px] border-primary bg-accent p-[26px]">
                  <div className="text-xs font-bold text-primary">Investimento</div>
                  <div className="mt-2.5 text-[29px] font-bold tracking-[-0.04em]">
                    {priceLine || product.pricing}
                  </div>
                  {priceNote && (
                    <div className="mt-1 text-[12.5px] text-muted-foreground">{priceNote}</div>
                  )}
                  {included.length > 0 && (
                    <div className="mt-5 flex flex-col gap-[9px]">
                      {included.map((item) => (
                        <div key={item} className="flex items-start gap-[9px]">
                          <span className="mt-0.5 text-xs font-bold text-primary">✓</span>
                          <span className="text-[13px] leading-[1.5] text-muted-foreground">
                            {item}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Link
                    to="/contato"
                    className="mt-[22px] inline-flex h-[46px] w-full items-center justify-center rounded-xl bg-primary text-[14px] font-bold text-primary-foreground transition hover:brightness-95"
                  >
                    Quero esse produto
                  </Link>
                  <div className="mt-[11px] text-center text-[11.5px] text-muted-foreground">
                    Diagnóstico gratuito antes de fechar
                  </div>
                </div>
                <div className="rounded-[18px] border border-border bg-card p-[22px]">
                  <div className="text-[12.5px] font-bold">Prazo de implantação</div>
                  <div className="mt-1.5 text-[13.5px] leading-[1.6] text-muted-foreground">
                    {product.deadline ||
                      "Combinado com você no diagnóstico gratuito, incluindo migração de dados e treinamento da equipe."}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <FooterNew />
    </div>
  );
};

export default ProdutoDetail;
